import { currentAttribution } from "@app/attribution.ts";
import { reportNetworkFailure, reportNetworkSuccess } from "@app/state/network.ts";
import type {
  GuessResult,
  PlayCurrent,
  PlayerStats,
  PublicRound,
} from "@shared/types";

export interface PublicResult {
  roundNumber: number;
  bands: ("green" | "yellow" | "red")[];
  scores: number[];
  total: number;
  viewer: { total: number; shareId: string | null } | null;
}

export interface GuessResponse {
  result: GuessResult;
  completed: boolean;
  totalScore: number | null;
  percentile: number | null;
  currentStreak: number;
  shareId: string | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: "include", ...init });
  } catch {
    // fetch solo lanza en fallos de red (sin conexión, DNS, corte).
    reportNetworkFailure();
    throw new ApiError(0, "offline", "Sin conexión");
  }
  reportNetworkSuccess();
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) code = body.error;
    } catch {
      /* sin cuerpo */
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  round: () => request<PublicRound>("/api/round/current"),
  playCurrent: () => request<PlayCurrent>("/api/play/current"),
  guess: (body: { roundId: string; position: number; lat: number; lng: number }) =>
    request<GuessResponse>("/api/play/guess", {
      method: "POST",
      headers: { "content-type": "application/json" },
      // El primer contacto solo se guarda al crear la partida (primer intento).
      body: JSON.stringify({ ...body, acq: currentAttribution() }),
    }),
  stats: () => request<PlayerStats>("/api/stats/me"),
  result: (shareId: string) => request<PublicResult>(`/api/result/${shareId}`),
};
